import React, { useEffect, useState } from "react";
import { api, formatErr, poundsFromPence } from "../lib/api";
import { useAttraction } from "../lib/attraction";
import DashboardLayout from "../components/DashboardLayout";
import { Save, Trash2, RefreshCw, CheckCircle2, AlertTriangle } from "lucide-react";
import { alertDialog, confirmDialog } from "../components/ConfirmDialog";
import { notify } from "../lib/notify";

const TABS = [
  { key: "business", label: "Business" },
  { key: "display", label: "Display" },
  { key: "stripe", label: "Stripe" },
  { key: "checkout", label: "Checkout" },
  { key: "notifications", label: "Notifications" },
  { key: "terms", label: "Terms & conditions" },
  { key: "staff", label: "Staff & roles" },
  { key: "analytics", label: "Analytics" },
];

export default function SettingsPage() {
  const initialTab = new URLSearchParams(window.location.search).get("tab") || "business";
  const [tab, setTab] = useState(TABS.some(t => t.key === initialTab) ? initialTab : "business");
  const [a, setA] = useState(null);
  const { refresh: refreshAttraction } = useAttraction() || {};

  const load = async () => { const r = await api.get("/attractions/me"); setA(r.data); };
  useEffect(() => { load(); }, []);

  const save = async (patch) => {
    try {
      const r = await api.patch("/attractions/me", patch);
      setA(r.data); notify.success("Saved");
      if (refreshAttraction) refreshAttraction();
    }
    catch (e) { notify.error(formatErr(e.response?.data?.detail) || "Save failed"); }
  };

  if (!a) return <DashboardLayout title="Settings" />;

  return (
    <DashboardLayout title="Settings" subtitle="Configure your attraction.">
      <div className="flex gap-2 mb-6 flex-wrap" data-testid="settings-tabs">
        {TABS.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)} data-testid={`tab-${t.key}`}
                  className={`px-4 py-2 rounded-lg text-sm font-semibold ${tab === t.key ? "bg-[#dc2626] text-white" : "bg-[#0e0e0e] border border-[#1a1a1a]"}`}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === "business" && <BusinessTab a={a} onSave={save} />}
      {tab === "display" && <DisplayTab a={a} onSave={save} />}
      {tab === "stripe" && <StripeTab />}
      {tab === "checkout" && <CheckoutTab a={a} onSave={save} />}
      {tab === "notifications" && <NotificationsTab a={a} onSave={save} />}
      {tab === "terms" && <TermsTab a={a} onSave={save} />}
      {tab === "staff" && <StaffTab />}
      {tab === "analytics" && <AnalyticsTab />}
    </DashboardLayout>
  );
}

function Card({ children }) { return <div className="st-card p-4">{children}</div>; }

const TIMEZONES = ["Europe/London", "Europe/Dublin", "Europe/Paris", "Europe/Berlin", "America/New_York", "America/Los_Angeles", "Australia/Sydney", "UTC"];

function NotificationsTab({ a, onSave }) {
  const [sms, setSms] = useState(a.sms_enabled !== false);
  const [tz, setTz] = useState(a.timezone || "Europe/London");
  const [status, setStatus] = useState(null);
  const [testPhone, setTestPhone] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api.get("/sms/status").then((r) => setStatus(r.data)).catch(() => setStatus({ configured: false }));
  }, []);

  const sendTest = async () => {
    if (!testPhone.trim()) { notify.error("Enter a mobile number (e.g. +447700900123)"); return; }
    setSending(true);
    try {
      await api.post("/sms/test", { phone: testPhone.trim() });
      notify.success("Test SMS sent — check the phone");
    } catch (e) {
      notify.error(formatErr(e.response?.data?.detail) || "Test send failed");
    } finally { setSending(false); }
  };

  return (
    <Card>
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm uppercase tracking-[0.18em] text-[#8a8a8a]">SMS notifications</h3>
        {status && (
          <span className={`text-xs px-2 py-1 rounded-full border ${status.configured
            ? "bg-[#0c1d12] border-[#1b3a25] text-[#86efac]"
            : "bg-[#1a1a1a] border-[#2a2a2a] text-[#8a8a8a]"}`} data-testid="sms-provider-status">
            {status.configured ? `Connected · ${status.sender}` : "Not configured"}
          </span>
        )}
      </div>
      <label className="flex items-start gap-3 cursor-pointer">
        <input type="checkbox" className="mt-1" checked={sms} onChange={(e) => setSms(e.target.checked)} data-testid="sms-enabled" />
        <span>
          <span className="block font-medium">Send SMS to customers</span>
          <span className="block text-sm text-[#8a8a8a]">Texts ticket links, reminders, payment links and cancellations to the phone numbers on bookings.</span>
        </span>
      </label>

      {status?.configured && (
        <div className="mt-4 p-3 rounded-lg bg-[#0e0e0e] border border-[#1a1a1a]">
          <div className="text-sm font-medium mb-2">Send a test message</div>
          <div className="flex flex-wrap gap-2 items-center">
            <input className="st-input max-w-xs" placeholder="+447700900123" value={testPhone}
                   onChange={(e) => setTestPhone(e.target.value)} data-testid="sms-test-phone" />
            <button className="st-btn" onClick={sendTest} disabled={sending} data-testid="sms-test-send">
              {sending ? "Sending…" : "Send test"}
            </button>
          </div>
          <p className="text-[11px] text-[#7a7a7a] mt-1.5">UK mobile in +44 format. Standard message rates apply.</p>
        </div>
      )}

      <div className="border-t border-[#1a1a1a] my-5" />
      <h3 className="text-sm uppercase tracking-[0.18em] text-[#8a8a8a] mb-2">Timezone</h3>
      <p className="text-sm text-[#8a8a8a] mb-3">Used to validate ticket check-ins against the visit date and to schedule reminder emails.</p>
      <select className="st-input max-w-xs" value={tz} onChange={(e) => setTz(e.target.value)} data-testid="timezone-select">
        {TIMEZONES.map((z) => <option key={z} value={z}>{z}</option>)}
      </select>

      <button className="st-btn mt-5 block" onClick={() => onSave({ sms_enabled: sms, timezone: tz })} data-testid="save-notifications"><Save size={14} /> Save</button>
    </Card>
  );
}

function DisplayTab({ a, onSave }) {
  const [tf, setTf] = useState(a.time_format || "12hr");
  const [td, setTd] = useState(a.timeslot_display || "start_end");
  const preview = tf === "12hr"
    ? (td === "start_only" ? "7pm" : "7:00pm – 7:15pm")
    : (td === "start_only" ? "19:00" : "19:00 – 19:15");
  return (
    <Card>
      <h3 className="text-sm uppercase tracking-[0.18em] text-[#8a8a8a] mb-4">Display preferences</h3>

      <div className="mb-6">
        <div className="text-sm font-semibold mb-2">Time format</div>
        <div className="space-y-2">
          {[["24hr","24-hour (e.g. 19:00)"],["12hr","12-hour (e.g. 7:00pm)"]].map(([k,l]) => (
            <label key={k} className="flex items-center gap-3 cursor-pointer">
              <input type="radio" name="tf" checked={tf === k} onChange={() => setTf(k)} data-testid={`tf-${k}`} />
              <span>{l}</span>
            </label>
          ))}
        </div>
      </div>

      <div className="mb-6">
        <div className="text-sm font-semibold mb-2">Time slot display</div>
        <div className="space-y-2">
          {[["start_only","Start time only (e.g. 7pm)"],["start_end","Start and end time (e.g. 7pm – 7:15pm)"]].map(([k,l]) => (
            <label key={k} className="flex items-center gap-3 cursor-pointer">
              <input type="radio" name="td" checked={td === k} onChange={() => setTd(k)} data-testid={`td-${k}`} />
              <span>{l}</span>
            </label>
          ))}
        </div>
      </div>

      <div className="bg-[#0d0d0d] border border-[#1a1a1a] rounded-lg p-4 mb-5">
        <div className="text-[10px] uppercase tracking-wider text-[#7a7a7a]">Preview</div>
        <div className="text-2xl font-bold mt-1 st-mono">{preview}</div>
      </div>

      <button className="st-btn" onClick={() => onSave({ time_format: tf, timeslot_display: td })} data-testid="save-display"><Save size={14}/> Save</button>
    </Card>
  );
}

function BusinessTab({ a, onSave }) {
  const [f, setF] = useState({ name: a.name, slug: a.slug, location: a.location, phone: a.phone, description: a.description, venue_postcode: a.venue_postcode || "" });
  return (
    <Card>
      <h3 className="text-sm uppercase tracking-[0.18em] text-[#8a8a8a] mb-4">Business profile</h3>
      <div className="grid md:grid-cols-2 gap-4">
        <Labeled label="Name"><input className="st-input" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="set-name" /></Labeled>
        <Labeled label="Slug"><input className="st-input" value={f.slug} onChange={(e) => setF({ ...f, slug: e.target.value })} data-testid="set-slug" /></Labeled>
        <Labeled label="Location"><input className="st-input" value={f.location} onChange={(e) => setF({ ...f, location: e.target.value })} /></Labeled>
        <Labeled label="Phone"><input className="st-input" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} /></Labeled>
        <Labeled label="Venue postcode">
          <input className="st-input" placeholder="e.g. LN1 1AA" value={f.venue_postcode}
                 onChange={(e) => setF({ ...f, venue_postcode: e.target.value })} data-testid="set-venue-postcode" />
          <span className="text-[11px] text-[#6a6a6a]">Used as the centre point for the Reach map's travel distances.</span>
        </Labeled>
        <div className="md:col-span-2"><Labeled label="Description"><textarea className="st-input min-h-[100px]" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} /></Labeled></div>
      </div>
      <button className="st-btn mt-5" onClick={() => onSave(f)} data-testid="save-business"><Save size={14}/> Save</button>
    </Card>
  );
}

function StripeTab() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try { const r = await api.get("/stripe/connect/status"); setStatus(r.data); }
    catch (e) { notify.error("Could not load Stripe status"); }
    setLoading(false);
  };
  useEffect(() => {
    load();
    const params = new URLSearchParams(window.location.search);
    const flag = params.get("stripe");
    if (flag === "connected") notify.success("Stripe account connected");
    else if (flag === "error") notify.error("Stripe connection failed. Please try again.");
    if (flag) {
      params.delete("stripe");
      window.history.replaceState({}, "", `${window.location.pathname}?${params.toString()}`.replace(/\?$/, ""));
    }
  }, []);

  const connect = async () => {
    setBusy(true);
    try {
      const r = await api.get("/stripe/connect/oauth-url", { params: { origin: window.location.origin } });
      window.location.href = r.data.url;
    } catch (e) {
      setBusy(false);
      await alertDialog({
        title: "Stripe not available yet",
        description: formatErr(e.response?.data?.detail) || "Payments are not configured on this platform yet. Add your Stripe keys to the backend environment to enable Connect.",
      });
    }
  };

  const disconnect = async () => {
    const ok = await alertDialog({
      title: "Disconnect Stripe?",
      description: "Customers will no longer be able to pay online until you reconnect.",
      confirmLabel: "Disconnect", cancel: true,
    });
    if (!ok) return;
    setBusy(true);
    try { await api.post("/stripe/connect/disconnect"); notify.success("Stripe disconnected"); await load(); }
    catch (e) { notify.error("Disconnect failed"); }
    setBusy(false);
  };

  if (loading) return <Card><div className="text-sm text-[#8a8a8a]">Loading…</div></Card>;

  return (
    <Card>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm uppercase tracking-[0.18em] text-[#8a8a8a]">Stripe Connect</h3>
        <span className="text-[11px] uppercase tracking-wider px-2 py-1 rounded bg-[#0e0e0e] border border-[#1a1a1a] text-[#8a8a8a]"
              data-testid="stripe-mode">{status?.mode === "live" ? "Live mode" : "Test mode"}</span>
      </div>

      {status?.diagnostics && (
        <details className="mb-4 rounded-lg border border-[#1a1a1a] bg-[#0e0e0e] p-3 text-xs" data-testid="stripe-diagnostics">
          <summary className="cursor-pointer text-[#8a8a8a] select-none">Connection diagnostics</summary>
          <div className="mt-2 space-y-1 font-mono text-[11px] text-[#bbb]">
            <div>Resolved mode: <span className={status.diagnostics.resolved_mode === "live" ? "text-[#86efac]" : "text-[#e8c987]"}>{status.diagnostics.resolved_mode}</span> (STRIPE_MODE env: {status.diagnostics.stripe_mode_env})</div>
            <div>Live secret key (sk_live): {status.diagnostics.live_secret_key_present ? "✅ present" : "❌ missing"}</div>
            <div>Live client id (STRIPE_CLIENT_ID_LIVE): {status.diagnostics.live_client_id_present ? "✅ present" : "❌ missing"}</div>
            <div>Live webhook secret: {status.diagnostics.live_webhook_secret_present ? "✅ present" : "❌ missing"}</div>
            <div>Test client id (STRIPE_CONNECT_CLIENT_ID): {status.diagnostics.test_client_id_present ? "present" : "absent"}</div>
            <div>Connect client id in use: <span className="text-[#dadada]">{status.diagnostics.client_id_in_use_prefix || "(none)"}</span></div>
          </div>
        </details>
      )}

      {!status?.enabled && (
        <div className="rounded-lg border border-[#3a2a0a] bg-[#1a1205] p-4 text-sm text-[#e8c987] mb-4" data-testid="stripe-not-configured">
          Stripe is not configured on the platform yet. Online card payments are disabled — bookings currently use the demo (no-charge) flow. Add your Stripe keys to enable Connect.
        </div>
      )}

      {status?.connected ? (
        <div data-testid="stripe-connected">
          <div className="flex items-center gap-2 text-[#86efac] font-semibold mb-1">
            <span className="inline-block w-2 h-2 rounded-full bg-[#22c55e]" /> Connected
          </div>
          <div className="text-sm text-[#bbb]">
            {status.account_name || status.account_email || status.account_id}
            {status.charges_enabled === false && (
              <span className="block text-[#e8c987] mt-1">⚠ Your Stripe account still needs to finish onboarding before it can accept charges.</span>
            )}
          </div>
          <div className="flex gap-2 mt-4">
            <a className="st-btn st-btn-ghost text-sm" href="https://dashboard.stripe.com" target="_blank" rel="noreferrer" data-testid="stripe-manage">Manage on Stripe</a>
            <button className="st-btn st-btn-ghost text-sm" onClick={disconnect} disabled={busy} data-testid="stripe-disconnect">Disconnect</button>
          </div>
        </div>
      ) : (
        <>
          {status?.needs_reconnect && (
            <div className="rounded-lg border border-[#3a2a0a] bg-[#1a1205] p-4 text-sm text-[#e8c987] mb-4" data-testid="stripe-needs-reconnect">
              ⚠ Your previous Stripe connection was made in <strong>{status.account_mode || "test"}</strong> mode, but the platform is now in <strong>{status.mode}</strong> mode. Test and live connections are separate on Stripe — please reconnect your account below to accept {status.mode === "live" ? "real" : "test"} payments.
            </div>
          )}
          <p className="text-sm text-[#bbb] mb-4">Connect your Stripe account to receive payouts directly. ScareTickets takes commission on each sale; the rest is paid straight to your account.</p>
          <button className="st-btn" onClick={connect} disabled={busy || !status?.enabled} data-testid="stripe-connect">
            {busy ? "Redirecting…" : (status?.needs_reconnect ? "Reconnect Stripe account" : "Connect Stripe account")}
          </button>
        </>
      )}
    </Card>
  );
}

function CheckoutTab({ a, onSave }) {
  const [policy, setPolicy] = useState(a.customer_account_policy);
  const [collectNames, setCollectNames] = useState(!!a.collect_attendee_names);
  const [attrWin, setAttrWin] = useState(a.attribution_window_days ?? 90);
  return (
    <Card>
      <h3 className="text-sm uppercase tracking-[0.18em] text-[#8a8a8a] mb-2">Customer account policy</h3>
      <p className="text-sm text-[#8a8a8a] mb-4">Controls whether customers need to create an account to complete a booking.</p>
      <div className="space-y-2">
        {[["guest_only","Guest only — no accounts"],["optional","Optional — guest or account"],["required","Account required — must register"]].map(([k,l]) => (
          <label key={k} className="flex items-center gap-3 cursor-pointer">
            <input type="radio" name="policy" checked={policy === k} onChange={() => setPolicy(k)} data-testid={`policy-${k}`} />
            <span>{l}</span>
          </label>
        ))}
      </div>

      <div className="border-t border-[#1a1a1a] my-5" />
      <h3 className="text-sm uppercase tracking-[0.18em] text-[#8a8a8a] mb-2">Attendee names</h3>
      <label className="flex items-start gap-3 cursor-pointer">
        <input type="checkbox" className="mt-1" checked={collectNames} onChange={(e) => setCollectNames(e.target.checked)} data-testid="collect-attendee-names" />
        <span>
          <span className="block font-medium">Collect attendee names</span>
          <span className="block text-sm text-[#8a8a8a]">When enabled, customers enter a name for each person in their group. Individual named tickets make it easier to distribute tickets to each attendee after booking.</span>
        </span>
      </label>

      <div className="border-t border-[#1a1a1a] my-5" />
      <h3 className="text-sm uppercase tracking-[0.18em] text-[#8a8a8a] mb-2">Attribution window</h3>
      <p className="text-sm text-[#8a8a8a] mb-3">How long we remember where a visitor first came from. If they return within this window, the original source/campaign still gets credit for the sale; after it, they're re-attributed to whatever brings them back.</p>
      <select className="st-input max-w-xs" value={String(attrWin)} onChange={(e) => setAttrWin(parseInt(e.target.value, 10))} data-testid="attribution-window-select">
        <option value="0">Never expire (keep original forever)</option>
        <option value="30">30 days</option>
        <option value="60">60 days</option>
        <option value="90">90 days (recommended)</option>
      </select>

      <button className="st-btn mt-5" onClick={() => onSave({ customer_account_policy: policy, collect_attendee_names: collectNames, attribution_window_days: attrWin })} data-testid="save-checkout"><Save size={14}/> Save</button>
    </Card>
  );
}

function TermsTab({ a, onSave }) {
  const [terms, setTerms] = useState(a.terms || "");
  return (
    <Card>
      <h3 className="text-sm uppercase tracking-[0.18em] text-[#8a8a8a] mb-4">Terms & conditions</h3>
      <textarea className="st-input min-h-[280px]" value={terms} onChange={(e) => setTerms(e.target.value)} data-testid="terms-input" />
      <button className="st-btn mt-3" onClick={() => onSave({ terms })} data-testid="save-terms"><Save size={14}/> Save</button>
    </Card>
  );
}

function StaffTab() {
  const [rows, setRows] = useState([]);
  const [appUrl, setAppUrl] = useState("");
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/staff-accounts");
      setRows(r.data.staff || []);
      setAppUrl(r.data.staff_app_url || "");
    } catch (e) { notify.error(formatErr(e.response?.data?.detail) || "Failed to load staff"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const fullUrl = appUrl ? `${window.location.origin}${appUrl}` : "";

  const add = async () => {
    if (!name.trim() || !/^\d{4,6}$/.test(pin)) {
      notify.error("Enter a name and a 4–6 digit PIN.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/staff-accounts", { name: name.trim(), pin });
      setName(""); setPin("");
      notify.success("Staff member added");
      load();
    } catch (e) { notify.error(formatErr(e.response?.data?.detail) || "Could not add staff"); }
    finally { setBusy(false); }
  };

  const resetPin = async (r) => {
    const np = window.prompt(`New PIN for ${r.name} (4–6 digits)`);
    if (np == null) return;
    if (!/^\d{4,6}$/.test(np)) { notify.error("PIN must be 4–6 digits."); return; }
    try { await api.patch(`/staff-accounts/${r.id}`, { pin: np }); notify.success("PIN updated"); load(); }
    catch (e) { notify.error(formatErr(e.response?.data?.detail) || "Failed"); }
  };

  const toggle = async (r) => {
    try { await api.patch(`/staff-accounts/${r.id}`, { active: !r.active }); load(); }
    catch (e) { notify.error(formatErr(e.response?.data?.detail) || "Failed"); }
  };

  const remove = async (r) => {
    const ok = await confirmDialog({
      title: `Remove ${r.name}?`,
      description: "They will be signed out on every device and can no longer check guests in.",
      confirmLabel: "Remove", destructive: true,
    });
    if (!ok) return;
    try { await api.delete(`/staff-accounts/${r.id}`); notify.success("Removed"); load(); }
    catch (e) { notify.error(formatErr(e.response?.data?.detail) || "Failed"); }
  };

  const printQrCard = async () => {
    try {
      const { data } = await api.get("/staff-accounts/login-qr", { params: { origin: window.location.origin } });
      const w = window.open("", "_blank", "width=480,height=680");
      if (!w) { notify.error("Allow pop-ups to print the card."); return; }
      w.document.write(`<!doctype html><html><head><title>Staff check-in — ${data.venue_name}</title>
        <style>
          *{box-sizing:border-box;font-family:-apple-system,Segoe UI,Roboto,Helvetica,Arial,sans-serif}
          body{margin:0;padding:32px;color:#0a0a0a;text-align:center}
          .card{max-width:380px;margin:0 auto;border:2px solid #0a0a0a;border-radius:18px;padding:28px}
          .brand{font-weight:800;letter-spacing:.04em;font-size:20px}
          .brand span{color:#dc2626}
          .venue{font-size:15px;color:#444;margin-top:2px}
          h1{font-size:22px;margin:18px 0 4px}
          .qr{width:220px;height:220px;margin:14px auto}
          .url{font-family:monospace;font-size:12px;word-break:break-all;color:#333;margin-top:8px}
          ol{text-align:left;font-size:13px;color:#333;line-height:1.6;margin:18px 4px 0;padding-left:20px}
          .foot{margin-top:16px;font-size:11px;color:#888}
        </style></head><body>
        <div class="card">
          <div class="brand">SCARE<span>TICKETS</span></div>
          <div class="venue">${data.venue_name}</div>
          <h1>Gate check-in app</h1>
          <img class="qr" src="${data.qr_data_uri}" alt="QR" />
          <div class="url">${data.url}</div>
          <ol>
            <li>Scan this code (or open the link) on your phone.</li>
            <li>Tap <b>Add to Home Screen</b> / <b>Install</b> to save the app.</li>
            <li>Sign in with your <b>name</b>, <b>PIN</b> and a <b>device name</b>.</li>
          </ol>
          <div class="foot">Ask your manager for your name and PIN.</div>
        </div>
        <script>window.onload=function(){setTimeout(function(){window.print();},300);}<\/script>
        </body></html>`);
      w.document.close();
    } catch (e) { notify.error(formatErr(e.response?.data?.detail) || "Could not build card"); }
  };

  return (
    <div className="space-y-4" data-testid="staff-tab">
      <Card>
        <h3 className="text-lg font-bold">Gate staff PINs</h3>
        <p className="text-sm text-[#8a8a8a] mt-1">
          Create a simple name + PIN for each gate worker. They sign into the check-in app on any phone — no dashboard access, check-in only.
        </p>
        {fullUrl && (
          <div className="mt-3 flex flex-col sm:flex-row sm:items-center gap-2 rounded-lg border border-[#1a1a1a] bg-[#0b0b0b] p-3">
            <div className="min-w-0 flex-1">
              <div className="text-xs text-[#8a8a8a] mb-0.5">Staff check-in app link</div>
              <div className="text-sm font-mono truncate" data-testid="staff-app-url">{fullUrl}</div>
            </div>
            <div className="flex gap-2 shrink-0">
              <button
                className="st-btn st-btn-ghost"
                onClick={() => { navigator.clipboard?.writeText(fullUrl); notify.success("Link copied"); }}
                data-testid="copy-staff-app-url"
              >Copy link</button>
              <button className="st-btn" onClick={printQrCard} data-testid="print-staff-qr-card">Print QR card</button>
            </div>
          </div>
        )}
      </Card>

      <Card>
        <h4 className="font-semibold mb-3">Add a staff member</h4>
        <div className="flex flex-col sm:flex-row gap-2">
          <input className="st-input flex-1" placeholder="Name (e.g. Gary)" value={name}
                 onChange={(e) => setName(e.target.value)} data-testid="staff-add-name" />
          <input className="st-input sm:w-40" placeholder="PIN (4–6 digits)" value={pin} inputMode="numeric"
                 onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 6))} data-testid="staff-add-pin" />
          <button className="st-btn" onClick={add} disabled={busy} data-testid="staff-add-submit">
            <Save size={14} /> Add
          </button>
        </div>
      </Card>

      <Card>
        <h4 className="font-semibold mb-3">Staff ({rows.length})</h4>
        {loading ? (
          <div className="text-sm text-[#8a8a8a]">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="text-sm text-[#8a8a8a]">No staff yet. Add your first gate worker above.</div>
        ) : (
          <div className="divide-y divide-[#161616]">
            {rows.map((r) => (
              <div key={r.id} className="flex items-center justify-between gap-2 py-3" data-testid={`staff-row-${r.id}`}>
                <div className="min-w-0">
                  <div className="font-medium flex items-center gap-2">
                    {r.name}
                    {!r.active && <span className="text-xs px-1.5 py-0.5 rounded bg-[#3a1212] text-[#f87171]">disabled</span>}
                  </div>
                  <div className="text-xs text-[#8a8a8a]">
                    {r.last_login_at ? `Last in ${new Date(r.last_login_at).toLocaleString("en-GB")}${r.last_device ? ` · ${r.last_device}` : ""}` : "Never signed in"}
                  </div>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <button className="st-btn st-btn-ghost !px-2.5 !py-1.5 text-xs" onClick={() => resetPin(r)} data-testid={`staff-reset-${r.id}`}>Reset PIN</button>
                  <button className="st-btn st-btn-ghost !px-2.5 !py-1.5 text-xs" onClick={() => toggle(r)} data-testid={`staff-toggle-${r.id}`}>{r.active ? "Disable" : "Enable"}</button>
                  <button className="st-btn st-btn-ghost !px-2 !py-1.5 text-xs" onClick={() => remove(r)} data-testid={`staff-delete-${r.id}`}><Trash2 size={14} /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

function AnalyticsTab() {
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = async () => {
    setRows(null);
    try { const r = await api.get("/dashboard/funnel/paid-events"); setRows(r.data.paid_events); }
    catch { setRows([]); }
  };
  useEffect(() => { load(); }, []);

  const del = async (row) => {
    const ok = window.confirm(
      row.has_real_order
        ? `This "Paid" event IS linked to a real order (${row.order_number}). Removing it will reduce your funnel's paid count. Delete anyway?`
        : `Remove this phantom "Paid" event? It has no real confirmed order behind it, so this corrects your funnel count.`
    );
    if (!ok) return;
    setBusy(row.id);
    try {
      await api.delete(`/dashboard/funnel/paid-events/${row.id}`);
      notify.success("Paid event removed");
      await load();
    } catch (e) { notify.error(formatErr(e.response?.data?.detail) || "Delete failed"); }
    finally { setBusy(null); }
  };

  return (
    <Card>
      <div className="flex items-start justify-between gap-3 mb-1">
        <h3 className="text-sm uppercase tracking-[0.18em] text-[#8a8a8a]">Conversion funnel — Paid events</h3>
        <button className="st-btn st-btn-ghost" onClick={load} data-testid="paid-events-refresh"><RefreshCw size={14} /> Refresh</button>
      </div>
      <p className="text-[13px] text-[#8a8a8a] mb-4 max-w-2xl">
        Every genuinely completed order records one "Paid" event here. If an old phantom event slipped in
        (from before the tracking fix) it shows as <span className="text-[#f59e0b]">No real order</span> — safe to remove to correct your funnel count.
      </p>

      {rows === null ? (
        <div className="text-[#7a7a7a] text-sm py-6">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="text-[#8a8a8a] text-sm py-6" data-testid="paid-events-empty">No paid funnel events recorded yet.</div>
      ) : (
        <div className="space-y-2" data-testid="paid-events-list">
          {rows.map((r) => (
            <div key={r.id} data-testid={`paid-event-${r.id}`}
                 className={`flex items-center gap-3 rounded-xl border p-3 ${r.has_real_order ? "border-[#1e1e1e] bg-[#0f0f0f]" : "border-[#3a1414] bg-[#1a0d0d]"}`}>
              <div className="shrink-0">
                {r.has_real_order
                  ? <CheckCircle2 size={18} className="text-[#22c55e]" />
                  : <AlertTriangle size={18} className="text-[#f59e0b]" />}
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium truncate">
                  {r.has_real_order
                    ? <>Order <span className="st-mono text-[#dc2626]">{r.order_number}</span> · {r.order_status}</>
                    : <span className="text-[#f59e0b]">No real order (phantom)</span>}
                </div>
                <div className="text-xs text-[#8a8a8a] truncate">
                  {(r.created_at || "").slice(0, 16).replace("T", " ")} · {r.source}{r.campaign ? ` / ${r.campaign}` : ""} · {r.tickets} tix · {poundsFromPence(r.revenue_pence)}
                </div>
              </div>
              <button className="st-btn st-btn-ghost shrink-0" disabled={busy === r.id}
                      onClick={() => del(r)} data-testid={`paid-event-delete-${r.id}`}>
                <Trash2 size={14} /> {busy === r.id ? "Removing…" : "Remove"}
              </button>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

function Labeled({ label, children }) {
  return <label className="block"><span className="text-xs text-[#8a8a8a]">{label}</span><div className="mt-1">{children}</div></label>;
}
